import { FocusMonitor } from '@angular/cdk/a11y';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { FormBuilder, NgControl } from '@angular/forms';
import { MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';

import { BaseMatControlComponent } from '../../base-mat-control.component';

interface SelectNode {
  name: string;
  value: any;
  children?: SelectNode[];
}

@Component({
  selector: 'app-easy-select-expandable',
  templateUrl: './easy-select-expandable.component.html',
  styleUrls: ['./easy-select-expandable.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: EasySelectExpandableComponent,
    },
  ],
})
export class EasySelectExpandableComponent
  extends BaseMatControlComponent<Array<any> | any>
  implements AfterViewInit
{
  @Input() options: Array<any> = [];
  @Input() hasEmptyOption: boolean = true;
  @Input() multiple = false;
  @Input() limit: number = 0;
  @Input() labelAttribute = '';
  @Input() disabledMatTooltip = '';
  @Input() valueAttribute = '';
  @Input() groupLabelAttribute = '';
  @Input() groupOptionsAttribute = '';
  @Input() showSelectedLabels = false;

  @ViewChild('easySelect') easySelect!: MatSelect;

  get limitReached(): boolean {
    return this.limit > 0 && this.value?.length >= this.limit;
  }

  private _transformer = (node: SelectNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      value: node.value,
      level: level,
    };
  };

  treeControl = new FlatTreeControl<any>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  hasChild = (_: number, node: any) => node.expandable;

  constructor(
    @Optional() @Self() public override ngControl: NgControl,
    protected override formBuilder: FormBuilder,
    protected override focusMonitor: FocusMonitor,
    protected override elRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {
    super(ngControl, formBuilder, focusMonitor, elRef);

    this.controlIdentity = 'easy-select';
    this.controlType = 'mat-select';
  }

  ngAfterViewInit() {
    this.userAriaDescribedBy = `${this.id}-required-error`;
    this.dataSource.data = this.options.map((option) => this.buildTree(option));
    this.treeControl.dataNodes
      // ha nincs nyitva minden, akkor kiválasztáskor ugrál --> TODO
      // .filter((node) => node.level === 0)
      .forEach((node) => {
        this.treeControl.expand(node);
      });
    this.cdr.detectChanges();
  }

  buildTree(option: any) {
    if (
      this.groupOptionsAttribute &&
      option[this.groupOptionsAttribute]?.length
    ) {
      const flatOption = { ...option };
      flatOption[this.labelAttribute] = flatOption[this.groupLabelAttribute];
      delete flatOption[this.groupOptionsAttribute];
      return {
        name: option[this.groupLabelAttribute],
        value: flatOption,
        children: option[this.groupOptionsAttribute].map((opt: any) =>
          this.buildTree(opt)
        ),
      };
    } else {
      return {
        name: option[this.labelAttribute],
        value: option,
        children: [],
      };
    }
  }

  // TODO ne scrollozódjon el, ha alfában választunk

  // TODO billentyűzetes navigálás
  onSelectField(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.easySelect.close();
    }
  }

  groupChange(e: any, node: any) {
    const groupValue = node.value.value;
    const subOptions = (this.dataSource as any)._flattenedData._value
      .filter(
        (option: any) =>
          option.value[this.valueAttribute]?.indexOf(groupValue) === 0 &&
          option.value[this.valueAttribute] !== groupValue &&
          !option.value[this.groupLabelAttribute] &&
          !option.value.disabled
      )
      .map((option: any) => option.value[this.valueAttribute]);
    if (!this.value) {
      this.value = [];
    }
    if (e.checked) {
      const checkableOptions = subOptions.filter(
        (option: any) => !this.value.includes(option)
      );
      this.value = [...this.value, ...checkableOptions];
    } else {
      const newValue = this.value.filter(
        (option: any) => !subOptions.includes(option)
      );
      this.value = newValue;
    }
  }

  isGroupChecked(node: any): boolean {
    const groupValue = node.value.value;
    const subOptions = (this.dataSource as any)._flattenedData._value
      .filter(
        (option: any) =>
          option.value[this.valueAttribute]?.indexOf(groupValue) === 0 &&
          option.value[this.valueAttribute] !== groupValue &&
          !option.value[this.groupLabelAttribute]
      )
      .map((option: any) => option.value[this.valueAttribute]);
    if (!this.value) {
      this.value = [];
    }
    let checked = true;
    subOptions.forEach((option: any) => {
      if (!this.value.includes(option)) {
        checked = false;
      }
    });
    return checked;
  }

  removeSelected(itemToRemove: any) {
    this.value = this.value.filter(
      (item: any) => JSON.stringify(item) !== JSON.stringify(itemToRemove)
    );
    this.cdr.detectChanges();
  }
}
